import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchContacts } from "services/customers.service";

interface ContactOption {
  id: string;
  name: string;
  position?: string;
  email?: string;
}

interface CustomerContactMultiSelectProps {
  companyId?: string;
  customerId: string;
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  isRequired?: boolean;
  error?: string;
  disabled?: boolean;
  /**
   * Contact ids already selected that must not be un-ticked — mirrors
   * MultipleUserSelect's lockedValueIds (template-generated document: add
   * signers freely, never remove an existing one). Ticking a new contact is
   * unaffected.
   */
  lockedIds?: string[];
}

const DEFAULT_LOCKED_IDS: string[] = [];

type FetchStatus = "idle" | "loading" | "success" | "error";

/**
 * Multi-select counterpart to CustomerContactSelect: same data source
 * (fetchContacts — server-side isAuthorizedToSign filter) and labels, but
 * lets several contacts of the same customer be picked (e.g. the executor
 * and their manager) instead of just one.
 *
 * Plain Tailwind checkbox list — customers have few authorized contacts, so
 * a list is the right control here. A catalyst-ui-kit combobox was tried
 * first, but it renders its options into a body portal (breaks under the
 * Polaris modal's z-index), ships Tailwind v4-only classes (this project is
 * on Tailwind 3.4), and Escape inside it closes the whole Polaris modal
 * instead of just the option list.
 */
export default function CustomerContactMultiSelect({
  companyId,
  customerId,
  value,
  onChange,
  label = "select_contact",
  isRequired = false,
  error: propsError = "",
  disabled = false,
  lockedIds = DEFAULT_LOCKED_IDS,
}: CustomerContactMultiSelectProps) {
  const { t } = useTranslation();
  // Every caller used to omit `companyId`, so requests went to
  // /companies/undefined/... (403) — fall back to the route param.
  const { companyId: routeCompanyId } = useParams();
  const effectiveCompanyId = companyId ?? routeCompanyId;

  const [options, setOptions] = useState<ContactOption[]>([]);
  const [status, setStatus] = useState<FetchStatus>("idle");
  const [touched, setTouched] = useState(false);
  // Blocks the one-option auto-select from re-firing after the user has
  // interacted with the list (e.g. deliberately unchecked it).
  const userInteractedRef = useRef(false);

  useEffect(() => {
    userInteractedRef.current = false;
    let cancelled = false;
    if (!effectiveCompanyId || !customerId) {
      setOptions([]);
      setStatus("idle");
      return;
    }
    setStatus("loading");
    (async () => {
      try {
        const data = await fetchContacts(effectiveCompanyId, customerId);
        if (cancelled) return;
        const collection: ContactOption[] = (data.contactsCollection || []).map(
          (row: any) => ({
            id: row.id != null ? String(row.id) : "",
            name: row.name || row.email || "",
            position: row.position || undefined,
            email: row.email || undefined,
          })
        );
        setOptions(collection);
        setStatus("success");
      } catch (e) {
        if (cancelled) return;
        setOptions([]);
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyId, customerId]);

  // Drop selected ids that turned out not to belong to this customer's
  // contact list (customer changed, or a contact was removed/disabled) —
  // this also empties `value` when the fetch succeeded with zero contacts.
  // Only once loading has actually succeeded: never while still loading,
  // and never on a failed fetch — a parent may have pre-filled `value` from
  // existing signers before the list is confirmed.
  useEffect(() => {
    if (status !== "success") return;
    const validIds = new Set(options.map((o) => o.id));
    const filtered = value.filter((id) => validIds.has(id));
    if (filtered.length !== value.length) {
      onChange(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, status, value]);

  // Same behaviour the old single-select had: pre-fill the only option when
  // the field is required and nothing is picked yet — but only once, so it
  // never fights a user who deliberately cleared the selection.
  useEffect(() => {
    if (
      isRequired &&
      status === "success" &&
      options.length === 1 &&
      value.length === 0 &&
      !userInteractedRef.current
    ) {
      onChange([options[0].id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, status, value, isRequired]);

  const toggle = (id: string) => {
    if (disabled) return;
    if (value.includes(id) && lockedIds.includes(id)) return;
    userInteractedRef.current = true;
    setTouched(true);
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const showRequiredError =
    touched && isRequired && status === "success" && value.length === 0;
  const error = propsError || (showRequiredError ? t("field_is_required") : "");

  return (
    <div>
      <label className="text-base/6 text-zinc-950 select-none sm:text-sm/6 dark:text-white">
        {t(label)}
        {isRequired && <span className="text-red-600"> *</span>}
      </label>
      <div className="mt-2 rounded-lg border border-zinc-950/10 dark:border-white/10 divide-y divide-zinc-950/5 dark:divide-white/5">
        {status === "idle" || status === "loading" ? (
          <div className="px-3 py-2.5 text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400">
            {t("loading")}
          </div>
        ) : status === "error" ? (
          <div className="px-3 py-2.5 text-base/6 text-red-600 sm:text-sm/6 dark:text-red-500">
            {t("fetch_error")}
          </div>
        ) : options.length === 0 ? (
          <div className="px-3 py-2.5 text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400">
            {t("no_contacts_authorized_to_sign")}
          </div>
        ) : (
          options.map((option) => {
            const checked = value.includes(option.id);
            const isLocked = checked && lockedIds.includes(option.id);
            const meta = [
              option.position || undefined,
              option.email && option.email !== option.name
                ? option.email
                : undefined,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <label
                key={option.id}
                className={`flex items-start gap-2.5 px-3 py-2.5 text-base/6 sm:text-sm/6 ${
                  disabled || isLocked
                    ? "cursor-not-allowed text-zinc-400 dark:text-zinc-600"
                    : "cursor-pointer hover:bg-zinc-950/[.025] dark:hover:bg-white/[.025]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled || isLocked}
                  onChange={() => toggle(option.id)}
                  className="mt-0.5 size-4 shrink-0 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 dark:border-zinc-600 dark:bg-transparent"
                />
                <span className="min-w-0">
                  <span className="block truncate text-zinc-950 dark:text-white">
                    {option.name}
                  </span>
                  {meta && (
                    <span className="block truncate text-xs text-zinc-500 dark:text-zinc-400">
                      {meta}
                    </span>
                  )}
                </span>
              </label>
            );
          })
        )}
      </div>
      {error && (
        <p className="mt-2 text-base/6 text-red-600 sm:text-sm/6 dark:text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}
